import { isPublicLogoTemplateId } from '@/logo-templates/registry';
import type { SiteLogo } from '@/types/shared';

export type SelectedLogo =
  | { source: 'template'; templateId: string }
  | { source: 'asset'; assetId: string; folderId?: string }
  | { source: 'ai'; assetId: string };

export type ComparableLogo = {
  text: string | null;
  source: SiteLogo['source'] | null;
  templateId?: string | null;
  assetId?: string | null;
};

export function isInactiveLogoTemplate(logo?: SiteLogo | null): boolean {
  const templateId = logo?.templateId;
  if (logo?.source !== 'template' || !templateId) return false;
  return !isPublicLogoTemplateId(templateId);
}

/** Compare logo selection for unsaved-changes; omit imageUrl and template assetId. */
export function normalizeComparableLogo(logo: SiteLogo | null): ComparableLogo | null {
  if (!logo) return null;

  const base: ComparableLogo = {
    text: logo.text ?? null,
    source: logo.source ?? null,
  };

  if (logo.source === 'template') {
    return { ...base, templateId: logo.templateId ?? null };
  }

  if (logo.source === 'asset' || logo.source === 'ai') {
    return { ...base, assetId: logo.assetId ?? null };
  }

  return base;
}

export function resolveSiteLogoSelection(logo?: SiteLogo | null): SelectedLogo | null {
  if (!logo?.source) return null;

  switch (logo.source) {
    case 'template': {
      if (!logo.templateId) return null;
      if (isInactiveLogoTemplate(logo)) {
        return logo.assetId
          ? { source: 'asset', assetId: logo.assetId, folderId: logo.assetFolderId }
          : null;
      }
      return { source: 'template', templateId: logo.templateId };
    }
    case 'asset':
      return logo.assetId
        ? { source: 'asset', assetId: logo.assetId, folderId: logo.assetFolderId }
        : null;
    case 'ai':
      return logo.assetId ? { source: 'ai', assetId: logo.assetId } : null;
  }
}

export function resolveLogoSelectionFromFields(fields: {
  logoSource?: SiteLogo['source'] | null;
  logoTemplateId?: string | null;
  logoAssetId?: string | null;
  logoAssetFolderId?: string | null;
}): SelectedLogo | null {
  return resolveSiteLogoSelection({
    source: fields.logoSource ?? undefined,
    templateId: fields.logoTemplateId ?? undefined,
    assetId: fields.logoAssetId ?? undefined,
    assetFolderId: fields.logoAssetFolderId ?? undefined,
  });
}

export function isSameLogoSelection(
  firstSelection: SelectedLogo | null,
  secondSelection: SelectedLogo | null,
): boolean {
  if (!firstSelection || !secondSelection) return false;

  if (firstSelection.source !== secondSelection.source) return false;

  switch (firstSelection.source) {
    case 'template':
      return (
        secondSelection.source === 'template' &&
        firstSelection.templateId === secondSelection.templateId
      );
    case 'asset':
      return (
        secondSelection.source === 'asset' && firstSelection.assetId === secondSelection.assetId
      );
    case 'ai':
      return secondSelection.source === 'ai' && firstSelection.assetId === secondSelection.assetId;
  }
}

export function hasLogoSelectionChanged(
  selectedLogo: SelectedLogo | null,
  siteLogo: SiteLogo | undefined,
  logoName: string,
): boolean {
  if (!selectedLogo) return false;

  const currentSiteSelection = resolveSiteLogoSelection(siteLogo);
  if (!isSameLogoSelection(selectedLogo, currentSiteSelection)) {
    return true;
  }

  const trimmedLogoName = logoName.trim();
  const trimmedSiteText = siteLogo?.text?.trim() ?? '';
  return trimmedLogoName !== trimmedSiteText;
}
